import * as React from 'react';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { FORGE_PORTAL_HOME } from '@helpcenter/forge-ui-utils/src/constants';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot } from 'view/common/element-root';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import type {
	forgeElementFragment$key,
	HelpLayoutForgeElementPages,
} from './__generated__/forgeElementFragment.graphql';

export interface ForgeElementViewerProps {
	forgeElementRef: forgeElementFragment$key;
	/**
	 * Project id of the current portal. Injected from Redux (see `hocs.tsx`) and
	 * forwarded to the Forge header/footer modules so that, on portal pages, the
	 * fetch-extensions request carries the portal resource context
	 * (`{ resourceId, resourceType: 'PROJECT' }`) — matching the behaviour of the
	 * classic (non-hub) portal home page.
	 */
	projectId?: number;
}
export const ForgeElement: React.FC<ForgeElementViewerProps> = ({ forgeElementRef, projectId }) => {
	di(useIsPageInEditMode, ForgeFooterModule, ForgeHeaderModule);

	const [isPageInEditMode] = useIsPageInEditMode();
	const forgeElementData = useFragment(
		graphql`
			fragment forgeElementFragment on HelpLayoutForgeElement {
				forgeElementType
				forgeElementPage
			}
		`,
		forgeElementRef,
	);
	const { forgeElementType, forgeElementPage } = forgeElementData;
	const isValidForgeElement =
		forgeElementType && ['HEADER_AND_SUBHEADER', 'FOOTER'].includes(forgeElementType);

	const propsForForgeModules = React.useMemo(
		() => ({
			// eslint-disable-next-line relay/no-future-added-value
			page: forgeElementPage as Exclude<HelpLayoutForgeElementPages, '%future added value'>,
			noWrapper: true, // this is to ignore extra spacings done from within the Forge Components
			allowCustomPadding: true,
			// Only portal pages carry a portal resource context (see projectId docs above).
			...(forgeElementPage === FORGE_PORTAL_HOME ? { projectId } : {}),
		}),
		[forgeElementPage, projectId],
	);

	React.useEffect(() => {
		if (!isValidForgeElement) {
			trackError(
				'forge.element.type.invalid',
				{ forgeElementType },
				'Invalid Forge Element Type received',
			);
		}
	}, [isValidForgeElement, forgeElementType]);

	const getForgeElement = React.useCallback(() => {
		if (forgeElementType === 'HEADER_AND_SUBHEADER') {
			return <ForgeHeaderModule {...propsForForgeModules} />;
		}
		if (forgeElementType === 'FOOTER') {
			return <ForgeFooterModule {...propsForForgeModules} />;
		}
		return null;
	}, [forgeElementType, propsForForgeModules]);

	return isValidForgeElement ? (
		<ElementRoot removePadding removeMargin={isIncreasedFlexibilityEnabled()}>
			<Box xcss={isPageInEditMode ? [forgeElementEditStyle] : undefined}>{getForgeElement()}</Box>
		</ElementRoot>
	) : null;
};

const forgeElementEditStyle = xcss({
	pointerEvents: 'none',
});
